/**
 * GuestSessionsPanel
 * Expanded body of a guest row: pick a session, see its answers and photos.
 */

import { motion, useReducedMotion } from 'framer-motion';
import type { PhotoSession } from '@/services/guest.service';
import type { Guest, Questionnaire } from '@/types';
import { formatShortDateTime, toDate } from '@/utils/guestFormat';
import { SessionDetail } from './SessionDetail';

type GuestSessionsPanelProps = {
  guest: Guest;
  sessions: PhotoSession[] | undefined;
  isLoading: boolean;
  error: string | null | undefined;
  selectedSessionId: string | null | undefined;
  onSelectSession: (sessionId: string) => void;
  onRetry: () => void;
  questionnairesById: Record<string, Questionnaire>;
  onOpenPhoto: (url: string) => void;
};

export function GuestSessionsPanel({
  guest,
  sessions,
  isLoading,
  error,
  selectedSessionId,
  onSelectSession,
  onRetry,
  questionnairesById,
  onOpenPhoto,
}: GuestSessionsPanelProps) {
  const reduceMotion = useReducedMotion();

  if (error) {
    return (
      <div className="guest-panel-inner">
        <div className="guest-panel-state is-error" role="alert">
          <p>Couldn’t load sessions: {error}</p>
          <button
            type="button"
            className="guest-retry-button"
            onClick={onRetry}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !sessions) {
    return (
      <div className="guest-panel-inner">
        <SessionsSkeleton />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="guest-panel-inner">
        <div className="guest-panel-state">
          <p>No sessions yet.</p>
        </div>
      </div>
    );
  }

  const selected =
    sessions.find((s) => s.id === selectedSessionId) ?? sessions[0];
  const questionnaire = selected.questionnaireId
    ? questionnairesById[selected.questionnaireId]
    : undefined;

  return (
    <div className="guest-panel-inner">
      {sessions.length > 1 && (
        <div
          className="session-tabs"
          role="tablist"
          aria-label={`Sessions for ${guest.email}`}
        >
          {sessions.map((session, index) => {
            const isActive = session.id === selected.id;
            const createdAt = toDate(session.createdAt);
            return (
              <button
                key={session.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`session-tab ${isActive ? 'is-active' : ''}`}
                onClick={() => onSelectSession(session.id)}
              >
                {isActive && (
                  <motion.span
                    className="session-tab-active"
                    layoutId={`session-tab-active-${guest.id}`}
                    transition={
                      reduceMotion
                        ? { duration: 0 }
                        : { type: 'spring', stiffness: 500, damping: 40 }
                    }
                  />
                )}
                <span
                  className={`status-dot ${session.emailStatus}`}
                  aria-hidden="true"
                />
                <span className="session-tab-label">
                  {createdAt ? formatShortDateTime(createdAt) : 'Unknown'}
                  {index === 0 && (
                    <span className="session-tab-latest"> · Latest</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <SessionDetail
        key={selected.id}
        session={selected}
        questionnaire={questionnaire}
        onOpenPhoto={onOpenPhoto}
      />
    </div>
  );
}

function SessionsSkeleton() {
  return (
    <div
      className="sessions-skeleton"
      aria-busy="true"
      aria-label="Loading sessions"
    >
      <div className="sessions-skeleton-tabs">
        <span className="sb-skeleton sessions-skeleton-tab" />
        <span className="sb-skeleton sessions-skeleton-tab" />
      </div>
      <div className="sessions-skeleton-body">
        <div className="sessions-skeleton-lines">
          <span
            className="sb-skeleton sessions-skeleton-line"
            style={{ width: '40%' }}
          />
          <span
            className="sb-skeleton sessions-skeleton-line"
            style={{ width: '70%' }}
          />
          <span
            className="sb-skeleton sessions-skeleton-line"
            style={{ width: '35%' }}
          />
          <span
            className="sb-skeleton sessions-skeleton-line"
            style={{ width: '60%' }}
          />
        </div>
        <div className="sessions-skeleton-photos">
          <span className="sb-skeleton sessions-skeleton-photo" />
          <span className="sb-skeleton sessions-skeleton-photo" />
          <span className="sb-skeleton sessions-skeleton-photo" />
          <span className="sb-skeleton sessions-skeleton-photo is-strip" />
        </div>
      </div>
    </div>
  );
}
